/**
 * Screen Time — per child, four panels:
 *   - Daily / weekly caps + emergency passthrough toggle
 *   - App rules: block / allow / per-app limit
 *   - Mode schedules: bedtime / school / focus / pause
 *   - Usage: 14-day bar chart + top-10 apps over the last week
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Plus,
  Trash2,
  Clock,
  Moon,
  GraduationCap,
  Target,
  Pause as PauseIcon,
  type LucideIcon,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { usersApi } from '@/api/users';
import {
  controlsApi,
  type AppRule,
  type DailyUsageRow,
  type AppUsageRow,
  type Mode,
  type ModeSchedule,
} from '@/api/controls';
import { ChildPicker } from './ChildPicker';
import { FocusPanel } from './FocusPanel';
import { PageHeader } from './PageHeader';
import { useSetFocusedChild } from './KidFocusContext';
import { cn } from '@/lib/utils';

interface ModeMeta {
  value: Mode;
  label: string;
  hint: string;
  icon: LucideIcon;
  /** Hex pair for "chip background / chip foreground" — also drives the timeline band. */
  color: { bg: string; fg: string; bar: string };
}

const MODES: ModeMeta[] = [
  {
    value: 'bedtime',
    label: 'Bedtime',
    hint: 'Device pauses overnight.',
    icon: Moon,
    color: { bg: 'bg-indigo-100', fg: 'text-indigo-700', bar: '#6366f1' },
  },
  {
    value: 'school',
    label: 'School',
    hint: 'Distracting apps blocked during class.',
    icon: GraduationCap,
    color: { bg: 'bg-sky-100', fg: 'text-sky-700', bar: '#0ea5e9' },
  },
  {
    value: 'focus',
    label: 'Focus',
    hint: 'Custom focus window (homework, etc.).',
    icon: Target,
    color: { bg: 'bg-emerald-100', fg: 'text-emerald-700', bar: '#10b981' },
  },
  {
    value: 'pause',
    label: 'Pause',
    hint: 'Family time — only emergency apps work.',
    icon: PauseIcon,
    color: { bg: 'bg-amber-100', fg: 'text-amber-800', bar: '#f59e0b' },
  },
];

function modeMeta(m: Mode): ModeMeta {
  return MODES.find((x) => x.value === m) ?? MODES[0]!;
}

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const ALL_DAYS = 0x7F;

export function ScreenTimePage() {
  const { t } = useTranslation();
  const children = useQuery({ queryKey: ['children'], queryFn: () => usersApi.listChildren() });
  const [selectedChildId, setSelectedChildId] = useState<string | null>(null);
  // Publish to Ask Kio so "how is she doing?" resolves to this child.
  useSetFocusedChild(selectedChildId);

  return (
    <div className="px-6 py-8 md:px-10 md:py-10">
      <PageHeader
        title={t('pages.screen_time.title')}
        icon={Clock}
        tint="green"
        description={t('pages.screen_time.description')}
      />

      <div className="mt-6">
        <ChildPicker
          children={children.data ?? []}
          selectedId={selectedChildId}
          onSelect={setSelectedChildId}
        />
      </div>

      {!selectedChildId && (
        <Card className="mt-6">
          <CardContent className="p-8 text-center text-sm text-[color:var(--kz-color-ink-secondary)]">
            {children.data?.length === 0 ? 'Add a child first.' : 'Select a child.'}
          </CardContent>
        </Card>
      )}

      {selectedChildId && (
        <Tabs defaultValue="limits" className="mt-6">
          <TabsList>
            <TabsTrigger value="limits">Limits</TabsTrigger>
            <TabsTrigger value="apps">App rules</TabsTrigger>
            <TabsTrigger value="schedules">Schedules</TabsTrigger>
            <TabsTrigger value="focus">Focus</TabsTrigger>
            <TabsTrigger value="usage">Usage</TabsTrigger>
          </TabsList>

          <TabsContent value="limits">
            <LimitsPanel childId={selectedChildId} />
          </TabsContent>
          <TabsContent value="apps">
            <AppRulesPanel childId={selectedChildId} />
          </TabsContent>
          <TabsContent value="schedules">
            <SchedulesPanel childId={selectedChildId} />
          </TabsContent>
          <TabsContent value="focus">
            <FocusPanel childId={selectedChildId} />
          </TabsContent>
          <TabsContent value="usage">
            <UsagePanel childId={selectedChildId} />
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}

// ── Limits ────────────────────────────────────────────────────────────────────

function LimitsPanel({ childId }: { childId: string }) {
  const queryClient = useQueryClient();
  const policy = useQuery({
    queryKey: ['controls', 'policy', childId],
    queryFn: () => controlsApi.getPolicy(childId),
  });

  const [daily, setDaily] = useState<string>('');
  const [weekly, setWeekly] = useState<string>('');
  const [didHydrate, setDidHydrate] = useState(false);

  // Hydrate once when policy first loads.
  if (policy.data && !didHydrate) {
    setDaily(policy.data.daily_limit_minutes?.toString() ?? '');
    setWeekly(policy.data.weekly_limit_minutes?.toString() ?? '');
    setDidHydrate(true);
  }

  const update = useMutation({
    mutationFn: (body: Partial<{ daily_limit_minutes: number | null; weekly_limit_minutes: number | null; emergency_apps_passthrough: boolean }>) =>
      controlsApi.updatePolicy(childId, body),
    onSuccess: () => {
      toast.success('Limits saved.');
      void queryClient.invalidateQueries({ queryKey: ['controls', 'policy', childId] });
    },
    onError: () => toast.error('Save failed.'),
  });

  const parseMins = (v: string): number | null => {
    const n = parseInt(v, 10);
    return Number.isFinite(n) ? n : null;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Daily &amp; weekly limits</CardTitle>
        <CardDescription>
          Cap total screen time. Leave blank for no cap. Per-app limits in the next tab apply on
          top.
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4 md:grid-cols-2">
        <div className="grid gap-1.5">
          <Label htmlFor="daily">Daily limit (minutes)</Label>
          <Input
            id="daily"
            type="number"
            min={0}
            max={1440}
            placeholder="e.g. 120"
            value={daily}
            onChange={(e) => setDaily(e.target.value)}
          />
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="weekly">Weekly limit (minutes)</Label>
          <Input
            id="weekly"
            type="number"
            min={0}
            max={10080}
            placeholder="e.g. 700"
            value={weekly}
            onChange={(e) => setWeekly(e.target.value)}
          />
        </div>
        <div className="md:col-span-2 flex items-center justify-between rounded-md border border-[color:var(--kz-color-border-subtle)] p-3">
          <div>
            <p className="text-sm font-medium">Emergency apps always work</p>
            <p className="text-xs text-[color:var(--kz-color-ink-muted)]">
              Calls to parents, SOS, maps, family chat — never blocked, regardless of any mode.
            </p>
          </div>
          <Switch
            checked={policy.data?.emergency_apps_passthrough ?? true}
            onCheckedChange={(v) => update.mutate({ emergency_apps_passthrough: v })}
            disabled={update.isPending}
          />
        </div>
        <div className="md:col-span-2">
          <Button
            onClick={() =>
              update.mutate({
                daily_limit_minutes: parseMins(daily),
                weekly_limit_minutes: parseMins(weekly),
              })
            }
            disabled={update.isPending}
          >
            {update.isPending ? 'Saving…' : 'Save limits'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ── App rules ─────────────────────────────────────────────────────────────────

function AppRulesPanel({ childId }: { childId: string }) {
  const queryClient = useQueryClient();
  const rules = useQuery({
    queryKey: ['controls', 'app-rules', childId],
    queryFn: () => controlsApi.listAppRules(childId),
  });

  const [appId, setAppId] = useState('');
  const [label, setLabel] = useState('');
  const [action, setAction] = useState<'allow' | 'block'>('block');
  const [limit, setLimit] = useState('');

  const add = useMutation({
    mutationFn: () =>
      controlsApi.addAppRule(childId, {
        app_id: appId.trim(),
        label: label.trim() || undefined,
        action,
        daily_limit_minutes: limit ? parseInt(limit, 10) : null,
      }),
    onSuccess: () => {
      setAppId('');
      setLabel('');
      setLimit('');
      void queryClient.invalidateQueries({ queryKey: ['controls', 'app-rules', childId] });
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : 'Add failed.'),
  });

  const remove = useMutation({
    mutationFn: (ruleId: string) => controlsApi.removeAppRule(childId, ruleId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['controls', 'app-rules', childId] }),
  });

  return (
    <div className="grid gap-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Add an app rule</CardTitle>
          <CardDescription>
            Use the platform package ID — Android (e.g. <code>com.google.android.youtube</code>) or iOS bundle ID.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (appId.trim()) add.mutate();
            }}
            className="grid gap-3 md:grid-cols-[2fr_1.5fr_1fr_1fr_auto] md:items-end"
          >
            <div className="grid gap-1.5">
              <Label htmlFor="app-id">App ID</Label>
              <Input
                id="app-id"
                placeholder="com.example.app"
                value={appId}
                onChange={(e) => setAppId(e.target.value)}
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="app-label">Label</Label>
              <Input
                id="app-label"
                placeholder="YouTube"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="app-action">Action</Label>
              <select
                id="app-action"
                className="flex h-9 w-full rounded-md border border-[color:var(--kz-color-border-subtle)] bg-transparent px-3 text-sm"
                value={action}
                onChange={(e) => setAction(e.target.value as 'allow' | 'block')}
              >
                <option value="block">Block</option>
                <option value="allow">Allow</option>
              </select>
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="app-limit">Daily min</Label>
              <Input
                id="app-limit"
                type="number"
                min={0}
                max={1440}
                placeholder="30"
                value={limit}
                onChange={(e) => setLimit(e.target.value)}
              />
            </div>
            <Button type="submit" size="sm" disabled={!appId.trim() || add.isPending}>
              <Plus className="size-4" />
              Add
            </Button>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Current app rules</CardTitle>
        </CardHeader>
        <CardContent>
          {rules.data?.length === 0 && (
            <p className="rounded-md border border-dashed border-[color:var(--kz-color-border-subtle)] p-3 text-xs text-[color:var(--kz-color-ink-muted)]">
              No app rules yet.
            </p>
          )}
          <ul className="divide-y divide-[color:var(--kz-color-border-subtle)]">
            {(rules.data ?? []).map((r: AppRule) => (
              <li key={r.id} className="flex items-center gap-3 py-2.5">
                <span
                  className={
                    r.action === 'block'
                      ? 'inline-flex shrink-0 items-center rounded-full bg-rose-100 px-2 py-0.5 text-xs font-medium text-rose-800'
                      : 'inline-flex shrink-0 items-center rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-800'
                  }
                >
                  {r.action}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="truncate text-sm font-medium">{r.label ?? r.app_id}</p>
                  <p className="truncate font-mono text-xs text-[color:var(--kz-color-ink-muted)]">
                    {r.app_id}
                  </p>
                </div>
                {r.daily_limit_minutes != null && (
                  <span className="shrink-0 rounded-full bg-[color:var(--kz-color-surface-sunken)] px-2 py-0.5 text-xs">
                    {r.daily_limit_minutes} min/day
                  </span>
                )}
                <Button variant="ghost" size="icon" onClick={() => remove.mutate(r.id)} aria-label="Remove">
                  <Trash2 className="size-4" />
                </Button>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}

// ── Schedules ─────────────────────────────────────────────────────────────────

function SchedulesPanel({ childId }: { childId: string }) {
  const queryClient = useQueryClient();
  const schedules = useQuery({
    queryKey: ['controls', 'schedules', childId],
    queryFn: () => controlsApi.listSchedules(childId),
  });

  // Wait for the query to resolve before mounting ScheduleCards. Otherwise
  // each card's useState() locks in the schedule={null} snapshot and never
  // syncs when real data arrives — the switch would be stuck on the default
  // (true) while the actual saved value is disabled.
  if (schedules.isPending) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-40 w-full" />
        <div className="grid gap-4 md:grid-cols-2">
          <Skeleton className="h-80 w-full" />
          <Skeleton className="h-80 w-full" />
          <Skeleton className="h-80 w-full" />
          <Skeleton className="h-80 w-full" />
        </div>
      </div>
    );
  }

  const byMode = new Map((schedules.data ?? []).map((s) => [s.mode, s]));
  const saved = schedules.data ?? [];
  const enabledToday = saved.filter(
    (s) => s.enabled && (s.days_of_week & (1 << ((new Date().getDay() + 6) % 7))),
  );

  return (
    <div className="space-y-4">
      <ScheduleTimeline schedules={enabledToday} />

      <div className="grid gap-4 md:grid-cols-2">
        {MODES.map(({ value, label, hint, icon, color }) => (
          <ScheduleCard
            key={value}
            childId={childId}
            mode={value}
            label={label}
            hint={hint}
            icon={icon}
            color={color}
            schedule={byMode.get(value) ?? null}
            invalidate={() =>
              queryClient.invalidateQueries({ queryKey: ['controls', 'schedules', childId] })
            }
          />
        ))}
      </div>
    </div>
  );
}

/** Horizontal 24-hour band showing every saved+enabled schedule active today. */
function ScheduleTimeline({ schedules }: { schedules: ModeSchedule[] }) {
  const now = new Date();
  const nowPct = ((now.getHours() * 60 + now.getMinutes()) / 1440) * 100;
  const ticks = [0, 6, 12, 18, 24];

  // For a schedule like 21:00 → 07:00 we draw TWO bands (21:00→24:00 and
  // 00:00→07:00) so the cross-midnight case still reads correctly today.
  const bands: { mode: Mode; left: number; width: number; meta: ModeMeta }[] = [];
  for (const s of schedules) {
    const [sh, sm] = s.start_time.split(':').map(Number) as [number, number];
    const [eh, em] = s.end_time.split(':').map(Number) as [number, number];
    const startMin = sh * 60 + sm;
    const endMin = eh * 60 + em;
    const meta = modeMeta(s.mode);
    if (startMin <= endMin) {
      bands.push({
        mode: s.mode,
        left: (startMin / 1440) * 100,
        width: ((endMin - startMin) / 1440) * 100,
        meta,
      });
    } else {
      bands.push({ mode: s.mode, left: (startMin / 1440) * 100, width: ((1440 - startMin) / 1440) * 100, meta });
      bands.push({ mode: s.mode, left: 0, width: (endMin / 1440) * 100, meta });
    }
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-end justify-between gap-3">
          <div>
            <CardTitle className="text-base">Today at a glance</CardTitle>
            <CardDescription>All enabled schedules for today, on a 24-hour band.</CardDescription>
          </div>
          <p className="text-xs font-medium text-[color:var(--kz-color-ink-muted)]">
            Now: {now.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
          </p>
        </div>
      </CardHeader>
      <CardContent>
        {bands.length === 0 ? (
          <p className="rounded-md border border-dashed border-[color:var(--kz-color-border-subtle)] p-3 text-sm text-[color:var(--kz-color-ink-muted)]">
            No schedules active today. Create one below — common picks are Bedtime
            (e.g. 21:00 → 07:00 every day) and School (Mon–Fri 08:00 → 15:00).
          </p>
        ) : (
          <>
            <div className="relative h-12 w-full overflow-hidden rounded-lg bg-[color:var(--kz-color-surface-sunken)]">
              {bands.map((b, i) => (
                <span
                  key={`${b.mode}-${i}`}
                  className="absolute top-0 h-full rounded-md opacity-90"
                  style={{
                    left: `${b.left}%`,
                    width: `${b.width}%`,
                    backgroundColor: b.meta.color.bar,
                  }}
                  title={`${b.meta.label}`}
                />
              ))}
              {/* "Now" indicator. */}
              <span
                className="absolute top-0 h-full w-0.5 bg-[color:var(--kz-color-ink-primary)]"
                style={{ left: `${nowPct}%` }}
                aria-label="Now"
              />
              <span
                className="absolute top-0 -translate-y-full -translate-x-1/2 rounded-full bg-[color:var(--kz-color-ink-primary)] px-1.5 py-0.5 text-[9px] font-bold text-white"
                style={{ left: `${nowPct}%` }}
              >
                NOW
              </span>
            </div>
            {/* Hour ticks. */}
            <div className="relative mt-1 h-3 w-full text-[10px] text-[color:var(--kz-color-ink-muted)]">
              {ticks.map((h) => (
                <span
                  key={h}
                  className="absolute -translate-x-1/2 tabular-nums"
                  style={{ left: `${(h / 24) * 100}%` }}
                >
                  {h.toString().padStart(2, '0')}:00
                </span>
              ))}
            </div>
            {/* Legend with currently-active highlights. */}
            <div className="mt-4 flex flex-wrap gap-3 text-xs">
              {schedules.map((s) => {
                const m = modeMeta(s.mode);
                const Icon = m.icon;
                const active = isScheduleActiveNow(s);
                return (
                  <span
                    key={s.mode}
                    className={cn(
                      'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 font-medium',
                      active ? 'ring-2 ring-emerald-400/60' : '',
                      m.color.bg,
                      m.color.fg,
                    )}
                  >
                    <Icon className="size-3.5" />
                    {m.label}
                    <span className="tabular-nums opacity-70">
                      {s.start_time.slice(0, 5)}→{s.end_time.slice(0, 5)}
                    </span>
                  </span>
                );
              })}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function ScheduleCard({
  childId,
  mode,
  label,
  hint,
  icon: Icon,
  color,
  schedule,
  invalidate,
}: {
  childId: string;
  mode: Mode;
  label: string;
  hint: string;
  icon: LucideIcon;
  color: ModeMeta['color'];
  schedule: ModeSchedule | null;
  invalidate: () => void;
}) {
  const exists = schedule !== null;
  const [start, setStart] = useState(schedule?.start_time?.slice(0, 5) ?? defaultStart(mode));
  const [end, setEnd] = useState(schedule?.end_time?.slice(0, 5) ?? defaultEnd(mode));
  const [days, setDays] = useState(schedule?.days_of_week ?? defaultDays(mode));
  // Local "enabled" reflects whatever's on the server (or true by default
  // before first save). Toggling autosaves once a schedule exists.
  const [enabled, setEnabled] = useState(schedule?.enabled ?? true);

  const upsert = useMutation({
    mutationFn: (override?: { enabled?: boolean }) =>
      controlsApi.upsertSchedule(childId, {
        mode,
        days_of_week: days,
        start_time: `${start}:00`,
        end_time: `${end}:00`,
        enabled: override?.enabled ?? enabled,
      }),
    onSuccess: () => {
      toast.success(`${label} saved.`);
      invalidate();
    },
    onError: () => toast.error('Save failed.'),
  });

  const remove = useMutation({
    mutationFn: () => controlsApi.removeSchedule(childId, mode),
    onSuccess: () => {
      toast.success(`${label} removed.`);
      invalidate();
    },
  });

  // Has the parent edited anything since the last save? Used to badge
  // "Unsaved changes" so they know to press Save.
  const dirty =
    exists &&
    (start !== (schedule?.start_time?.slice(0, 5) ?? '') ||
      end !== (schedule?.end_time?.slice(0, 5) ?? '') ||
      days !== (schedule?.days_of_week ?? 0));

  const isActiveNow = exists && enabled && isScheduleActiveNow(schedule);
  const summary = humanScheduleSummary({ start, end, days });

  // Day presets so parents don't have to click 5 buttons individually.
  const setPreset = (preset: 'all' | 'weekdays' | 'weekends') => {
    if (preset === 'all') setDays(ALL_DAYS);
    else if (preset === 'weekdays') setDays(0b00011111); // Mon-Fri
    else setDays(0b01100000); // Sat-Sun
  };

  return (
    <Card className={cn('relative overflow-hidden', !enabled && exists && 'opacity-75')}>
      {/* Color bar — visual key for the mode, matches the timeline band. */}
      <span
        aria-hidden
        className="absolute inset-x-0 top-0 h-1"
        style={{ backgroundColor: color.bar }}
      />
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3">
            <span
              className={cn(
                'grid size-10 shrink-0 place-items-center rounded-2xl',
                color.bg,
                color.fg,
              )}
            >
              <Icon className="size-5" />
            </span>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <CardTitle className="text-base">{label}</CardTitle>
                {isActiveNow && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-emerald-700">
                    <span className="size-1.5 rounded-full bg-emerald-500" />
                    Active now
                  </span>
                )}
                {!exists && (
                  <span className="rounded-full bg-[color:var(--kz-color-surface-sunken)] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-[color:var(--kz-color-ink-muted)]">
                    Not set
                  </span>
                )}
                {dirty && (
                  <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber-800">
                    Unsaved
                  </span>
                )}
              </div>
              <CardDescription className="mt-0.5">{hint}</CardDescription>
            </div>
          </div>
          <Switch
            checked={enabled}
            onCheckedChange={(v) => {
              setEnabled(v);
              if (exists) upsert.mutate({ enabled: v });
            }}
            aria-label={`${label} enabled`}
          />
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Human-readable summary of what the form currently says. */}
        <div
          className={cn(
            'flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-medium',
            enabled ? color.bg : 'bg-[color:var(--kz-color-surface-sunken)]',
            enabled ? color.fg : 'text-[color:var(--kz-color-ink-muted)]',
          )}
        >
          <Clock className="size-3.5 shrink-0" />
          <span className="truncate">
            {enabled ? summary : `${label} is off — nothing scheduled.`}
          </span>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="grid gap-1.5">
            <Label htmlFor={`${mode}-start`} className="text-xs">Start</Label>
            <Input
              id={`${mode}-start`}
              type="time"
              value={start}
              onChange={(e) => setStart(e.target.value)}
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor={`${mode}-end`} className="text-xs">End</Label>
            <Input
              id={`${mode}-end`}
              type="time"
              value={end}
              onChange={(e) => setEnd(e.target.value)}
            />
          </div>
        </div>
        <div>
          <div className="mb-1.5 flex items-center justify-between">
            <span className="text-xs font-medium text-[color:var(--kz-color-ink-secondary)]">Days</span>
            <div className="flex gap-1 text-[10px] font-medium">
              <button type="button" onClick={() => setPreset('all')} className="rounded-full bg-[color:var(--kz-color-surface-sunken)] px-2 py-0.5 hover:bg-[color:var(--kz-color-brand-primary)]/10 hover:text-[color:var(--kz-color-brand-primary)]">Every day</button>
              <button type="button" onClick={() => setPreset('weekdays')} className="rounded-full bg-[color:var(--kz-color-surface-sunken)] px-2 py-0.5 hover:bg-[color:var(--kz-color-brand-primary)]/10 hover:text-[color:var(--kz-color-brand-primary)]">Weekdays</button>
              <button type="button" onClick={() => setPreset('weekends')} className="rounded-full bg-[color:var(--kz-color-surface-sunken)] px-2 py-0.5 hover:bg-[color:var(--kz-color-brand-primary)]/10 hover:text-[color:var(--kz-color-brand-primary)]">Weekends</button>
            </div>
          </div>
          <div className="grid grid-cols-7 gap-1">
            {DAY_LABELS.map((d, i) => {
              const bit = 1 << i;
              const on = (days & bit) !== 0;
              return (
                <button
                  type="button"
                  key={d}
                  onClick={() => setDays(days ^ bit)}
                  className={cn(
                    'rounded-lg py-1.5 text-[11px] font-semibold transition-colors',
                    on
                      ? 'text-white shadow-sm'
                      : 'bg-[color:var(--kz-color-surface-sunken)] text-[color:var(--kz-color-ink-secondary)] hover:bg-[color:var(--kz-color-brand-primary)]/10',
                  )}
                  style={on ? { backgroundColor: color.bar } : undefined}
                >
                  {d[0]}
                </button>
              );
            })}
          </div>
        </div>
        <div className="flex gap-2 pt-1">
          <Button size="sm" onClick={() => upsert.mutate(undefined)} disabled={upsert.isPending}>
            <Clock className="size-4" />
            {exists ? 'Save changes' : `Create ${label.toLowerCase()}`}
          </Button>
          {exists && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => remove.mutate()}
              disabled={remove.isPending}
            >
              Remove
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function defaultStart(mode: Mode): string {
  return mode === 'school' ? '08:00' : mode === 'bedtime' ? '21:00' : '17:00';
}
function defaultEnd(mode: Mode): string {
  return mode === 'school' ? '15:00' : mode === 'bedtime' ? '07:00' : '19:00';
}
function defaultDays(mode: Mode): number {
  return mode === 'school' ? 0b00011111 : ALL_DAYS; // Mon-Fri default for school
}

const FULL_DAY_NAME = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

/** Render the days bitmap as a compact English phrase. */
function daysSummary(days: number): string {
  const set: number[] = [];
  for (let i = 0; i < 7; i++) if (days & (1 << i)) set.push(i);
  if (set.length === 0) return 'no days';
  if (set.length === 7) return 'every day';
  if (set.length === 5 && set.every((d) => d < 5)) return 'weekdays';
  if (set.length === 2 && days === 0b01100000) return 'weekends';
  return set.map((d) => FULL_DAY_NAME[d]?.slice(0, 3)).join(', ');
}

/** "Mon-Fri · 21:00 → 07:00 next morning" — what the form currently means. */
function humanScheduleSummary({ start, end, days }: { start: string; end: string; days: number }): string {
  const crossesMidnight = start > end;
  const tail = crossesMidnight ? ` ${end} next morning` : ` ${end}`;
  return `Active ${daysSummary(days)} · ${start} →${tail}`;
}

/** Does the current saved schedule match "now"? Same logic as the backend. */
function isScheduleActiveNow(s: ModeSchedule | null): boolean {
  if (!s || !s.enabled) return false;
  const now = new Date();
  // Backend uses Mon=0, Sun=6. JS Date.getDay(): Sun=0, Sat=6. Convert:
  const jsDay = now.getDay();
  const monBased = (jsDay + 6) % 7;
  if (!(s.days_of_week & (1 << monBased))) return false;
  const hh = now.getHours().toString().padStart(2, '0');
  const mm = now.getMinutes().toString().padStart(2, '0');
  const t = `${hh}:${mm}:00`;
  const sStart = s.start_time;
  const sEnd = s.end_time;
  if (sStart <= sEnd) return sStart <= t && t <= sEnd;
  return t >= sStart || t <= sEnd; // crosses midnight
}

// ── Usage ─────────────────────────────────────────────────────────────────────

function UsagePanel({ childId }: { childId: string }) {
  const daily = useQuery({
    queryKey: ['controls', 'usage-daily', childId],
    queryFn: () => controlsApi.usageDaily(childId, 14),
  });
  const apps = useQuery({
    queryKey: ['controls', 'usage-apps', childId],
    queryFn: () => controlsApi.usageApps(childId, 7, 10),
  });

  return (
    <div className="grid gap-4 md:grid-cols-[2fr_1.2fr]">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Last 14 days</CardTitle>
          <CardDescription>Total daily screen time.</CardDescription>
        </CardHeader>
        <CardContent>
          <DailyBars rows={daily.data ?? []} />
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Top apps · 7 days</CardTitle>
          <CardDescription>Most used apps this week.</CardDescription>
        </CardHeader>
        <CardContent>
          <AppList rows={apps.data ?? []} />
        </CardContent>
      </Card>
    </div>
  );
}

function DailyBars({ rows }: { rows: DailyUsageRow[] }) {
  if (rows.length === 0) {
    return (
      <p className="rounded-md border border-dashed border-[color:var(--kz-color-border-subtle)] p-3 text-xs text-[color:var(--kz-color-ink-muted)]">
        No usage reported yet.
      </p>
    );
  }
  const max = Math.max(...rows.map((r) => r.seconds), 1);
  return (
    <ul className="space-y-2">
      {rows.map((r) => (
        <li key={r.day} className="flex items-center gap-3 text-xs">
          <span className="w-24 shrink-0 tabular-nums text-[color:var(--kz-color-ink-secondary)]">
            {r.day}
          </span>
          <span className="relative h-5 flex-1 overflow-hidden rounded-sm bg-[color:var(--kz-color-surface-sunken)]">
            <span
              className="absolute inset-y-0 left-0 bg-[color:var(--kz-color-brand-primary)]/85"
              style={{ width: `${(r.seconds / max) * 100}%` }}
            />
          </span>
          <span className="w-16 shrink-0 text-right tabular-nums">
            {Math.round(r.seconds / 60)} min
          </span>
        </li>
      ))}
    </ul>
  );
}

function AppList({ rows }: { rows: AppUsageRow[] }) {
  if (rows.length === 0) {
    return (
      <p className="rounded-md border border-dashed border-[color:var(--kz-color-border-subtle)] p-3 text-xs text-[color:var(--kz-color-ink-muted)]">
        No app usage yet.
      </p>
    );
  }
  const max = Math.max(...rows.map((r) => r.seconds), 1);
  return (
    <ul className="space-y-2">
      {rows.map((r) => (
        <li key={r.app_id} className="grid gap-1 text-xs">
          <div className="flex items-center justify-between">
            <span className="truncate font-mono">{r.app_id}</span>
            <span className="tabular-nums text-[color:var(--kz-color-ink-secondary)]">
              {Math.round(r.seconds / 60)} min
            </span>
          </div>
          <span className="relative block h-1.5 w-full overflow-hidden rounded-full bg-[color:var(--kz-color-surface-sunken)]">
            <span
              className="absolute inset-y-0 left-0 bg-[color:var(--kz-color-brand-primary)]/85"
              style={{ width: `${(r.seconds / max) * 100}%` }}
            />
          </span>
        </li>
      ))}
    </ul>
  );
}
